import { injectable } from 'inversify';

import type { BaseDocumentModel, SearchDocumentResults } from '../models';
import { type MeiliSearchConfig, SearchRepository } from '../repositories';
import { createBaseMapper } from '../base-mapper';
import type { BaseDocumentVM } from '../view-models';
import type { BaseDocumentsRepository } from '../BaseDocuments.repository';
import { isForbiddenError } from '../errors';
import type { MeiliSearchParams } from '../types';

@injectable()
export class BaseSearchService<
  TViewModel extends BaseDocumentVM = BaseDocumentVM,
  TModel extends BaseDocumentModel = BaseDocumentModel,
> {
  protected mapper = createBaseMapper<TModel, TViewModel>();

  #searchRepo?: SearchRepository<TModel>;

  protected documentsRepo: BaseDocumentsRepository<TModel>;

  #searchParams: MeiliSearchConfig;

  protected get searchRepo() {
    if (!this.#searchRepo) {
      this.#searchRepo = new SearchRepository(this.#searchParams.baseURL);
    }

    return this.#searchRepo;
  }

  protected set searchRepo(repo) {
    this.#searchRepo = repo;
  }

  async search(filteringParams: MeiliSearchParams): Promise<SearchDocumentResults<TViewModel>> {
    if (!this.#searchParams || this.#searchParams.expiresAt <= new Date()) {
      this.#searchParams = await this.documentsRepo.getSearchConfig();
    }

    try {
      const searchResult = await this.searchRepo.search(filteringParams, this.#searchParams);

      return {
        ...searchResult,
        hits: searchResult.hits.map((h: { doc: TModel }) => {
          return { ...h, doc: this.mapper.toDomain(h.doc) };
        }),
      };
    } catch (error) {
      if (!isForbiddenError(error)) throw error;

      this.#searchParams.expiresAt = new Date(0);
      return this.search(filteringParams);
    }
  }
}
